/**
 * Rapor üretimi: ağırlıklı skor, kategori kırılımı, satır bazlı annotasyon.
 *
 * Skor kuralı: yalnızca "decisive" ve confidence ≥ medium olan issue'lar
 * cezalandırılır. Düşük güvenli bulgular kullanıcıya gösterilir ama karara
 * katılmaz; böylece "PDF parse edemedi diye makaleyi suçlama" ilkesi korunur.
 */

import type {
	AnalyzeOptions,
	CategoryScore,
	Confidence,
	ExtractedDocument,
	Issue,
	IssueCategory,
	Report,
	Severity
} from '../types';
import { DocumentIndex } from '../text/document';
import { confidenceWeight } from '../text/normalize';
import { runStatsEngine, clampScore } from '../stats/engine';
import { runReferenceEngine } from '../references/engine';
import { runStructureEngine } from '../structure/engine';
import { runLanguageEngine } from '../language/engine';

export const DEFAULT_WEIGHTS: Record<IssueCategory, number> = {
	statistics: 0.35,
	citations: 0.25,
	structure: 0.2,
	language: 0.15,
	provenance: 0.05
};

export const CATEGORY_LABELS: Record<IssueCategory, string> = {
	statistics: 'İstatistiksel raporlama',
	citations: 'Atıf ve kaynakça',
	structure: 'Yapısal bütünlük',
	language: 'Dil ve iddia düzeyi',
	provenance: 'Çıkarım güvenilirliği'
};

/** Severity başına taban ceza. */
const SEVERITY_PENALTY: Record<Severity, number> = {
	critical: 16,
	warning: 7,
	info: 2.5,
	ok: 0
};

export interface AnalysisResult {
	report: Report;
	index: DocumentIndex;
}

export function analyzeDocument(
	doc: ExtractedDocument,
	options: AnalyzeOptions = {}
): AnalysisResult {
	const index = new DocumentIndex(doc);

	const stats = runStatsEngine(index, {
		alpha: options.alpha,
		tolerance: options.pTolerance
	});
	const citations = runReferenceEngine(index);
	const structure = runStructureEngine(index);
	const language = runLanguageEngine(index);

	// Provenans bulguları
	const provenanceIssues = buildProvenanceIssues(doc);

	const allIssues: Issue[] = [
		...stats.issues,
		...citations.issues,
		...structure.issues,
		...language.issues,
		...provenanceIssues
	];

	// Çıkarım kalitesi düşükse tüm bulguların güvenini bir kademe düşür
	const downgraded = doc.quality.level === 'low';
	if (downgraded) {
		for (const issue of allIssues) {
			if (issue.category === 'provenance') continue;
			issue.confidence = downgrade(issue.confidence);
			issue.decisive = issue.decisive && issue.confidence !== 'low';
			issue.detail +=
				' (Belge metin kalitesi düşük olduğu için bu bulgunun güveni bir kademe düşürüldü.)';
		}
	}

	// Karar verilebilirlik: decisive && confidence >= medium && >= 1 kanıt
	for (const issue of allIssues) {
		if (issue.confidence === 'low') issue.decisive = false;
	}

	const weights = { ...DEFAULT_WEIGHTS, ...(options.weights ?? {}) };
	const categories = buildCategoryScores(allIssues, weights, {
		statsChecked: stats.result.findings.length > 0,
		referencesDetected: citations.result.references.length > 0,
		structureChecked: structure.result.sections.length >= 2,
		languageChecked: language.result.hits.length > 0 || language.result.design !== 'unknown'
	});

	const overall = clampScore(
		categories.reduce((sum, c) => sum + c.score * c.weight, 0) /
			Math.max(0.0001, categories.reduce((sum, c) => sum + c.weight, 0))
	);

	const counts: Record<Severity, number> = { critical: 0, warning: 0, info: 0, ok: 0 };
	for (const issue of allIssues) counts[issue.severity]++;

	const report: Report = {
		version: '1.0',
		generatedAt: new Date().toISOString(),
		document: {
			fileName: doc.fileName,
			format: doc.format,
			pages: doc.pageCount,
			chars: doc.text.length,
			quality: doc.quality
		},
		scores: {
			overall,
			grade: gradeOf(overall),
			categories
		},
		stats: stats.result,
		citations: citations.result,
		structure: structure.result,
		language: language.result,
		issues: allIssues.sort(compareIssues),
		counts,
		undecided: allIssues.filter((i) => !i.decisive).length
	};

	return { report, index };
}

function buildCategoryScores(
	issues: Issue[],
	weights: Record<IssueCategory, number>,
	checked: {
		statsChecked: boolean;
		referencesDetected: boolean;
		structureChecked: boolean;
		languageChecked: boolean;
	}
): CategoryScore[] {
	const cats: IssueCategory[] = ['statistics', 'citations', 'structure', 'language', 'provenance'];
	return cats.map((category) => {
		const catIssues = issues.filter((i) => i.category === category);
		const decisive = catIssues.filter((i) => i.decisive && i.confidence !== 'low');

		let score = 100;
		for (const issue of decisive) {
			const w = confidenceWeight(issue.confidence);
			score -= SEVERITY_PENALTY[issue.severity] * w;
		}
		// Aynı kodun çok sayıda tekrarı skoru sıfıra sürüklemesin: logaritmik tavan
		const count = decisive.length;
		if (count > 6) score += Math.min(25, (count - 6) * 2);

		let note: string | undefined;
		let isChecked = true;
		if (category === 'citations' && !checked.referencesDetected) {
			isChecked = false;
			score = 100;
			note = 'Kaynakça bölümü tespit edilemedi; atıf denetimi yapılamadı.';
		}
		if (category === 'statistics' && !checked.statsChecked) {
			isChecked = false;
			note = 'Raporlanmış istatistik bulunamadı.';
		}
		if (category === 'structure' && !checked.structureChecked) {
			isChecked = false;
			note = 'Bölüm yapısı çıkarılamadı.';
		}

		return {
			category,
			label: CATEGORY_LABELS[category],
			score: clampScore(score),
			weight: weights[category],
			contribution: clampScore(score) * weights[category],
			issueCount: catIssues.length,
			criticalCount: catIssues.filter((i) => i.severity === 'critical').length,
			checked: isChecked,
			note
		};
	});
}

function buildProvenanceIssues(doc: ExtractedDocument): Issue[] {
	const issues: Issue[] = [];
	const q = doc.quality;

	if (q.level === 'low') {
		issues.push({
			id: 'prov-1',
			code: 'prov.low_text_quality',
			category: 'provenance',
			severity: 'warning',
			title: 'Metin çıkarımı güvenilir değil',
			detail:
				`Metin kalitesi skoru ${Math.round(q.score * 100)}/100. ` +
				(q.notes.join(' ') || 'Nedeni belirlenemedi.') +
				' Bu belgedeki bulgular manuel doğrulama gerektirir.',
			confidence: 'high',
			decisive: true,
			location: {},
			meta: { score: q.score, notes: q.notes.join(' ') }
		});
	}

	if (q.emptyPages > 0) {
		issues.push({
			id: 'prov-2',
			code: 'prov.scanned_pdf',
			category: 'provenance',
			severity: q.emptyPages / Math.max(1, q.pages) > 0.4 ? 'warning' : 'info',
			title: `${q.emptyPages} sayfada metin katmanı yok`,
			detail:
				'Taranmış sayfalar analiz edilemez. OCR uygulanmış bir sürüm yüklemeyi deneyin.',
			confidence: 'high',
			location: {},
			decisive: q.emptyPages / Math.max(1, q.pages) > 0.4,
			meta: { emptyPages: q.emptyPages, pages: q.pages }
		});
	}

	if (q.garbageRatio > 0.01) {
		issues.push({
			id: 'prov-3',
			code: 'prov.missing_glyphs',
			category: 'provenance',
			severity: q.garbageRatio > 0.05 ? 'warning' : 'info',
			title: 'Tanınmayan glifler',
			detail: `Metnin %${(q.garbageRatio * 100).toFixed(1)}'i okunamayan karakter içeriyor (font/kodlama sorunu).`,
			confidence: 'high',
			location: {},
			decisive: q.garbageRatio > 0.05,
			meta: { ratio: q.garbageRatio }
		});
	}

	if (q.chars < 500) {
		issues.push({
			id: 'prov-4',
			code: 'prov.short_document',
			category: 'provenance',
			severity: 'warning',
			title: 'Belge çok kısa',
			detail: 'Analiz için yeterli metin yok; sonuçlar temsil edici değildir.',
			confidence: 'high',
			decisive: true,
			location: {}
		});
	}

	return issues;
}

function compareIssues(a: Issue, b: Issue): number {
	const sev = { critical: 0, warning: 1, info: 2, ok: 3 } as const;
	const s = sev[a.severity] - sev[b.severity];
	if (s !== 0) return s;
	// Aynı severity içinde önce kararlı olanlar
	if (a.decisive !== b.decisive) return a.decisive ? -1 : 1;
	return (a.location.charStart ?? Number.MAX_SAFE_INTEGER) - (b.location.charStart ?? Number.MAX_SAFE_INTEGER);
}

function gradeOf(score: number): Report['scores']['grade'] {
	if (score >= 90) return 'A';
	if (score >= 75) return 'B';
	if (score >= 60) return 'C';
	return 'D';
}

function downgrade(c: Confidence): Confidence {
	return c === 'high' ? 'medium' : 'low';
}
